import { createContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'

import type { ModalsIds } from '../constants'

/**
 * context 안에 들어갈 modal 정보
 ** id: ModalsIds - modal을 유니크하게 관리하는 id
 ** Component: any - modal Component
 ** props?: any - modal props
 ** path?: string - modal이 open 되었을 때 path
 */
interface ModalData {
  id: ModalsIds
  Component: any
  path?: string
  props?: any
}

/**
 * modal context interface
 ** modals: ModalData[] - modal들을 넣은 배열
 ** openModal: (modal: ModalData) => void - modal data를 받아 modal을 여는 함수
 ** closeModal: (id: ModalData['id']) => void - modal id를 받아 해당 modal을 닫는 함수
 */
interface Modals {
  modals: ModalData[]
  openModal: (modal: ModalData) => void
  closeModal: (id: ModalData['id']) => void
}

export const ModalContext = createContext<Modals>({
  modals: [],
  openModal: () => undefined,
  closeModal: () => undefined,
})

export const ModalProvider = ({ children }: { children: any }) => {
  const [modals, setModals] = useState<Modals['modals']>([])

  const location = useLocation()

  useEffect(() => {
    if (!modals.length) {
      return
    }

    setModals((prevState) => prevState.filter(({ path }) => path === location.pathname))
  }, [location])

  const openModal = (modal: ModalData) => {
    setModals((prevState) => [...prevState, { ...modal, path: location.pathname }])
  }

  const closeModal = (id: ModalData['id']) => {
    setModals((prevState) => prevState.filter((modal) => modal.id !== id))
  }

  return <ModalContext.Provider value={{ modals, openModal, closeModal }}>{children}</ModalContext.Provider>
}

export type { ModalData }
